import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NgIcon } from '@ng-icons/core';
import { createHttpContextFromOptions } from '@quipex/core/primitives';
import { qpxToast as toast } from '@quipex/core/utils';
import {
    BillingApiService,
    BuildingCheckoutBillingInterval,
    BuildingPricingMetricType,
} from '@quipex/shared/contracts';
import {
    DialogContent,
    QpxButton,
    QpxIcon,
    QpxSkeleton,
    QpxTypography,
    type DialogResult,
} from '@quipex/shared/ui';
import { EMPTY, catchError, from, tap } from 'rxjs';
import { CHECKOUT_REDIRECT, publishBuilding } from './api';
import {
    createPricingCalculationSummary,
    formatCheckoutPrice,
} from './pricing-calculation-summary';
import { PublishCapabilities } from './ui/capabilities';
import { PublishCheckoutActions } from './ui/checkout-actions';
import type {
    CapabilityRow,
    PlanCadence,
    PlanCard,
    PricingCalculationSummary,
} from './ui/models';
import { PublishPlanOptions } from './ui/plan-options';
import { PublishPricingCalculation } from './ui/pricing-calculation';

const CAPABILITY_ROWS: readonly CapabilityRow[] = [
    {
        label: 'Documents',
        draft: '1 GB cap',
        published: 'Expanded allowance',
    },
    {
        label: 'Quippy',
        draft: 'Limited credits',
        published: 'Monthly credits',
    },
    {
        label: 'Sharing',
        draft: 'Unavailable',
        published: 'User and org invites',
    },
] as const;

const CHECKOUT_BILLING_INTERVAL_BY_CADENCE = {
    monthly: BuildingCheckoutBillingInterval.Month,
    annual: BuildingCheckoutBillingInterval.Year,
} as const satisfies Record<PlanCadence, BuildingCheckoutBillingInterval>;

const FALLBACK_PRICING_MESSAGE =
    "We can't determine the number of lots or floor area, so this building is on fallback pricing.";

@Component({
    selector: 'qpx-publish-building-dialog',
    imports: [
        MatButtonModule,
        MatDialogModule,
        NgIcon,
        DialogContent,
        PublishCapabilities,
        PublishCheckoutActions,
        PublishPlanOptions,
        PublishPricingCalculation,
        QpxButton,
        QpxIcon,
        QpxSkeleton,
        QpxTypography,
    ],
    templateUrl: './publish-building-dialog.html',
    styleUrl: './publish-building-dialog.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublishBuildingCheckoutDialog {
    //#region Dependency injection
    private readonly billingApi = inject(BillingApiService);
    private readonly checkoutRedirect = inject(CHECKOUT_REDIRECT);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialogRef =
        inject<MatDialogRef<PublishBuildingCheckoutDialog, DialogResult>>(
            MatDialogRef
        );
    //#endregion

    //#region Inputs
    public readonly buildingId = input.required<string>();
    //#endregion

    //#region Constants
    protected readonly capabilityRows = CAPABILITY_ROWS;
    protected readonly fallbackPricingMessage = FALLBACK_PRICING_MESSAGE;
    //#endregion

    //#region State
    protected readonly isProcessing = signal(false);
    protected readonly selectedCadence = signal<PlanCadence>('annual');
    private readonly pricingPreview = rxResource({
        params: () => ({ buildingId: this.buildingId() }),
        stream: ({ params }) =>
            params
                ? this.billingApi.getBillingBuildingCheckoutPricingPreview(
                      params.buildingId,
                      {
                          context: createHttpContextFromOptions({
                              // The checkout dialog renders pricing failures with an explicit reload action.
                              suppressAutoErrorToast: true,
                          }),
                      }
                  )
                : EMPTY,
    });
    protected readonly pricingCards = computed<readonly PlanCard[]>(() => {
        const preview = this.pricingPreview.value();

        if (!preview) return [];

        return [
            {
                cadence: 'monthly',
                label: 'Monthly',
                price: formatCheckoutPrice(
                    preview,
                    preview.prices.monthly.amount
                ),
                priceCadence: '/ building',
                billingLabel: 'Billed monthly',
            },
            {
                cadence: 'annual',
                label: 'Annual',
                price: formatCheckoutPrice(
                    preview,
                    preview.prices.yearly.amount
                ),
                priceCadence: '/ building',
                billingLabel: 'Billed annually',
            },
        ] as const;
    });
    protected readonly checkoutActionLabel = computed(
        () => `Publish and secure with ${this.selectedCadence()} plan`
    );
    protected readonly isFallbackPricing = computed(
        () =>
            this.pricingPreview.value()?.metric.type ===
            BuildingPricingMetricType.FallbackFlatFee
    );
    protected readonly pricingCalculationSummary =
        computed<PricingCalculationSummary | null>(() => {
            const preview = this.pricingPreview.value();

            if (!preview) return null;

            return createPricingCalculationSummary(preview);
        });
    protected readonly isPricingLoading = computed(() =>
        this.pricingPreview.isLoading()
    );
    protected readonly hasPricingError = computed(
        () => !!this.pricingPreview.error()
    );
    protected readonly canContinueToCheckout = computed(
        () =>
            !this.isProcessing() &&
            !this.isPricingLoading() &&
            !this.hasPricingError() &&
            !!this.pricingPreview.value()
    );
    //#endregion

    //#region Template methods
    protected onCancel(): void {
        if (this.isProcessing()) return;

        this.dialogRef.close(false);
    }

    protected onContinueToCheckout(): void {
        if (!this.canContinueToCheckout()) return;

        this.isProcessing.set(true);

        const checkoutPromise = publishBuilding(
            this.billingApi,
            this.buildingId(),
            CHECKOUT_BILLING_INTERVAL_BY_CADENCE[this.selectedCadence()],
            this.checkoutRedirect
        );

        // eslint-disable-next-line agentic/prefer-auto-error-toast -- checkout suppresses the automatic HTTP toast so the dialog owns the Stripe handoff failure copy.
        toast.task(checkoutPromise, {
            loading: 'Preparing checkout...',
            success: 'Redirecting to Stripe Checkout...',
            error: {
                defaultMessage: 'Failed to start checkout',
            },
            finally: () => {
                this.isProcessing.set(false);
            },
        });

        from(checkoutPromise)
            .pipe(
                tap(() => {
                    this.dialogRef.close(true);
                }),
                catchError(() => EMPTY),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    protected reloadPricing(): void {
        this.pricingPreview.reload();
    }

    protected onSelectedCadenceChange(cadence: PlanCadence): void {
        this.selectedCadence.set(cadence);
    }
    //#endregion
}
